import { useState, useEffect } from "react";
import axios from "axios";
import { Alert, Box, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  getInputProps,
  getInputLabelProps,
  PopperFullWidth,
} from "../../common/appStyles";
import localize from "../../common/localize";
import Combobox from "./Combobox";
import useGlobalContext from "../../hooks/useGlobalContext";

import {
  GetDateInMMDDYYYYFormat,
  GetDateInYYYYMMDDWithoutTimeFormat,
  getFieldTypeName,
  getOperationalStatusNameFromId,
  getStatusText,
  getValueInCurrency,
} from "../../common/utils";
import {
  getCurrencyFromSetup,
  getMovementTypeName,
  getNewnessByCode,
  MovementTypeEnum,
} from "../../common/EntitiesManagement";
// import {
//   CLIENTS_API,
//   EVENTS_API,
//   OPERATIONAL_AREA_API,
//   WORKFLOW_ENDPOINT,
// } from "../../services/config";

export default function ModalFilter({
  filterOptions,
  selectedFilter,
  setSelectedFilter,
  onSubmit,
}) {
  const {
    config,
    locations,
    getCities,
    STATUS_ENDPOINT,
    ADDITIONAL_NEWNESS_ENDPOINT,
    EVENTTYPE_API,
    PERSONAL_API,
    CLIENTS_API,
    EVENTS_API,
    OPERATIONAL_AREA_API,
    WORKFLOW_ENDPOINT,
    CONTRACTOR_API,
    PROCEEDING_ENDPOINT,
    PROVIDER_ENDPOINT,
    REQUEST_TYPE_ENDPOINT,
    INVENTORY_MOVEMENT_TYPE_ENDPOINT,
    WAREHOUSE_ENDPOINT,
    getClientTypes,
  } = useGlobalContext();
  const [showInputSection, setShowInputsection] = useState(false);
  const [showDateRangeSection, setShowDateRangeSection] = useState(false);
  const [showInputValue, setShowInputValue] = useState(false);
  const [showAutoComplete, setShowAutoComplete] = useState(false);
  const [filterOptionsToSelect, setFilterOptionsToSelect] = useState(null);
  const [filterValues, setFilterValues] = useState(null);
  const [selectedFilterValue, setSelectedFilterValue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showValueRange, setShowValueRange] = useState(false);
  const [selectedState, setSelectedState] = useState(undefined);
  const [states, setStates] = useState(undefined);

  const getAdditionalNewness = async () => {
    setIsLoading(true);
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(
      `${ADDITIONAL_NEWNESS_ENDPOINT}/`,
      requestConfig,
    );

    const newnesses = {
      Data: [
        {
          Id: -1000,
          Name: getNewnessByCode("LOW-BATTERY"),
        },
        {
          Id: -1001,
          Name: getNewnessByCode("NORMAL-BATTERY"),
        },
        {
          Id: -1002,
          Name: getNewnessByCode("GPS-ON"),
        },
        {
          Id: -1003,
          Name: getNewnessByCode("GPS-OFF"),
        },
        {
          Id: -1004,
          Name: getNewnessByCode("AIRPLANE-ON"),
        },
        {
          Id: -1005,
          Name: getNewnessByCode("AIRPLANE-OFF"),
        },
      ],
    };

    if (data?.data?.Data && data?.data?.Data.length > 0) {
      data?.data.Data?.map((x) => newnesses.Data.push(x));
    }

    setFilterValues(newnesses);
    setIsLoading(false);
  };

  useEffect(() => {
    setFilterOptionsToSelect(filterOptions);
    handleFilterTypeOnChange(selectedFilter);
  }, []);

  const getEventType = async () => {
    setIsLoading(true);
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${EVENTTYPE_API}/`, requestConfig);

    setFilterValues(data?.data);
    setIsLoading(false);
  };

  const getRequestTypes = async () => {
    setIsLoading(true);
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${REQUEST_TYPE_ENDPOINT}/`, requestConfig);

    setFilterValues(data?.data);
    setIsLoading(false);
  };

  const getPaymentstatus = () => {
    setIsLoading(true);
    const data = {
      Data: [
        {
          Id: 1,
          Name: localize.payed,
        },
        {
          Id: 2,
          Name: localize.not_payed,
        },
        {
          Id: 0,
          Name: localize.without_payment,
        },
      ],
    };
    setFilterValues(data);
    setIsLoading(false);
  };

  const getBillStatus = () => {
    setIsLoading(true);
    const data = {
      Data: [
        {
          Id: 1,
          Name: localize.billed,
        },
        {
          Id: 2,
          Name: localize.not_billed,
        },
        {
          Id: 3,
          Name: localize.without_bill,
        },
      ],
    };
    setFilterValues(data);
    setIsLoading(false);
  };

  const getRequestStatus = () => {
    setIsLoading(true);
    const data = {
      Data: [
        {
          Id: 0,
          Name: getStatusText(0),
        },
        {
          Id: 1,
          Name: getStatusText(1),
        },
        {
          Id: 2,
          Name: getStatusText(2),
        },
        {
          Id: 3,
          Name: getStatusText(3),
        },
      ],
    };
    setFilterValues(data);
    setIsLoading(false);
  };

  const getContractors = async () => {
    setIsLoading(true);

    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${CONTRACTOR_API}/`, requestConfig);

    if (data?.data?.Ok) {
      const values = {
        Data: data?.data?.Data,
      };

      if (values?.Data && values.Data.length > 0) {
        setFilterValues({
          Data: [
            {
              Id: 0,
              Name: localize.without_contractor,
            },
            ...values.Data,
          ],
        });

        setIsLoading(false);
        return;
      }
    }

    setFilterValues({
      Data: [
        {
          Id: 0,
          Name: localize.without_contractor,
        },
      ],
    });
    setIsLoading(false);
  };

  const getClients = async () => {
    setIsLoading(true);

    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${CLIENTS_API}/`, requestConfig);

    if (data?.data?.Ok) {
      const values = {
        Data: data?.data?.Data,
      };

      if (values?.Data && values.Data.length > 0) {
        setFilterValues({
          Data: [
            {
              Id: 0,
              Name: localize.without_client,
            },
            ...values.Data,
          ],
        });

        setIsLoading(false);
        return;
      }
    }

    setFilterValues({
      Data: [
        {
          Id: 0,
          Name: localize.without_client,
        },
      ],
    });
    setIsLoading(false);
  };

  const getPersonal = async () => {
    setIsLoading(true);
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PERSONAL_API}/`, requestConfig);

    if (data?.data?.Ok) {
      const values = {
        Data: data?.data?.Data,
      };

      if (values?.Data && values.Data.length > 0) {
        setFilterValues({
          Data: [
            {
              id: 0,
              Name: localize.not_assigned_masc,
              SurName: localize.not_assigned_masc,
              Identification: "",
              LastName: "",
            },
            ...values.Data,
          ],
        });

        setIsLoading(false);
        return;
      }
    }

    setFilterValues({
      Data: [
        {
          id: 0,
          Name: localize.not_assigned_masc,
          SurName: localize.not_assigned_masc,
          Identification: "",
          LastName: "",
        },
      ],
    });

    setIsLoading(false);
  };

  const getStates = (filter) => {
    setIsLoading(true);
    if (locations?.Ok) {
      if (filter?.Name === "City") {
        setStates({ Data: locations.Data });
      } else {
        setFilterValues({ Data: locations.Data });
      }
    } else {
      if (filter?.Name === "City") {
        setStates({ Data: [] });
      } else {
        setFilterValues({ Data: [] });
      }
    }

    setIsLoading(false);
  };

  const getCitiesData = async (parentCode) => {
    setIsLoading(true);

    const result = await getCities(parentCode);

    if (result?.ok) {
      setFilterValues({ Data: result.data });
    } else {
      setFilterValues({ Data: [] });
    }

    setIsLoading(false);
  };

  const getOperationalStatus = () => {
    setIsLoading(true);
    const operationalStatuses = {
      Data: [
        {
          Id: 0,
          Name: getOperationalStatusNameFromId(0),
        },
        {
          Id: 1,
          Name: getOperationalStatusNameFromId(1),
        },
        {
          Id: 2,
          Name: getOperationalStatusNameFromId(2),
        },
        {
          Id: 3,
          Name: getOperationalStatusNameFromId(3),
        },
        {
          Id: 4,
          Name: getOperationalStatusNameFromId(4),
        },
        {
          Id: 5,
          Name: getOperationalStatusNameFromId(6),
        },
      ],
    };

    setFilterValues(operationalStatuses);
    setIsLoading(false);
  };

  const getPaymentMethods = async () => {
    setIsLoading(true);

    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${EVENTS_API}/paymentMethod`, requestConfig);
    setFilterValues(data?.data);
    setIsLoading(false);
  };

  const getOperationalAreas = async () => {
    setIsLoading(true);

    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${OPERATIONAL_AREA_API}`, requestConfig);
    setFilterValues(data?.data);
    setIsLoading(false);
  };

  const getWorkflows = async () => {
    setIsLoading(true);

    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${WORKFLOW_ENDPOINT}`, requestConfig);
    setFilterValues(data?.data);
    setIsLoading(false);
  };

  const getStatuses = async () => {
    setIsLoading(true);

    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${STATUS_ENDPOINT}/`, requestConfig);

    setFilterValues(data?.data);
    setIsLoading(false);
  };

  const getClientTypesData = async () => {
    setIsLoading(true);

    const clientT = await getClientTypes();

    setFilterValues({
      Data: clientT?.map((item) => ({ Id: item.Id, Name: item.Name })),
    });
    setIsLoading(false);
  };

  const getIdentificationTypes = () => {
    setFilterValues({
      Data: [
        {
          Id: "Cédula de ciudadanía",
          Name: "Cédula de ciudadanía",
        },
        { Id: "Nit", Name: "Nit" },
        { Id: "Pasaporte", Name: "Pasaporte" },
        {
          Id: "Cédula de extranjería",
          Name: "Cédula de extranjería",
        },
        { Id: "Otro", Name: "Otro" },
      ],
    });
  };

  const getFormFieldValueTypes = () => {
    setFilterValues({
      Data: [
        {
          Id: "STRING",
          Name: getFieldTypeName("STRING"),
        },
        {
          Id: "INT",
          Name: getFieldTypeName("INT"),
        },
        {
          Id: "DOUBLE",
          Name: getFieldTypeName("DOUBLE"),
        },
        {
          Id: "DATE",
          Name: getFieldTypeName("DATE"),
        },
        {
          Id: "TIME",
          Name: getFieldTypeName("TIME"),
        },
        {
          Id: "DATETIME",
          Name: getFieldTypeName("DATETIME"),
        },
        {
          Id: "SIGNATURE",
          Name: getFieldTypeName("SIGNATURE"),
        },
        {
          Id: "PHOTO",
          Name: getFieldTypeName("PHOTO"),
        },
        {
          Id: "BOOLEAN",
          Name: getFieldTypeName("BOOLEAN"),
        },
        {
          Id: "COMBOBOX",
          Name: getFieldTypeName("COMBOBOX"),
        },
      ],
    });
  };

  const getIsPrivateOptions = () => {
    setFilterValues({
      Data: [
        {
          Id: "true",
          Name: localize.is_private,
        },
        {
          Id: "false",
          Name: localize.is_public,
        },
      ],
    });
  };

  const getIsPublicOptions = () => {
    setFilterValues({
      Data: [
        {
          Id: "true",
          Name: localize.is_public,
        },
        {
          Id: "false",
          Name: localize.is_private,
        },
      ],
    });
  };

  const getTrueFalseOptions = () => {
    setFilterValues({
      Data: [
        {
          Id: "true",
          Name: localize.yes,
        },
        {
          Id: "false",
          Name: localize.no,
        },
      ],
    });
  };

  const getInventoryMovementTypes = async () => {
    setIsLoading(true);

    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(
      `${INVENTORY_MOVEMENT_TYPE_ENDPOINT}/`,
      requestConfig,
    );

    setFilterValues(data?.data);
    setIsLoading(false);
  };

  const getWarehouses = async () => {
    setIsLoading(true);

    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${WAREHOUSE_ENDPOINT}/`, requestConfig);

    setFilterValues(data?.data);
    setIsLoading(false);
  };

  const getProceeding = async () => {
    setIsLoading(true);

    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PROCEEDING_ENDPOINT}/`, requestConfig);

    setFilterValues(data?.data);
    setIsLoading(false);
  };

  const getProvider = async () => {
    setIsLoading(true);

    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PROVIDER_ENDPOINT}/`, requestConfig);

    setFilterValues(data?.data);

    setIsLoading(false);
  };

  const getMovementTypes = () => {
    const data = {
      Data: [
        {
          Id: MovementTypeEnum.In,
          Name: getMovementTypeName(MovementTypeEnum.In),
        },
        {
          Id: MovementTypeEnum.Out,
          Name: getMovementTypeName(MovementTypeEnum.Out),
        },
      ],
    };
    setFilterValues(data);
    setIsLoading(false);
  };

  const handleFilterTypeOnChange = (filter) => {
    if (!filter) {
      setShowInputsection(false);
      return;
    }

    setSelectedFilter(filter);
    setShowInputsection(true);
    setShowInputValue(false);
    setShowDateRangeSection(false);
    setShowAutoComplete(false);
    setShowValueRange(false);

    switch (filter.Name) {
      case "DateRange":
      case "OccurrTime":
      case "GenerationDate":
      case "CreationDate":
      case "ModificationDate":
      case "ExpectedSolutionDate":
      case "PaymentDate":
      case "BillDate":
      case "Soat":
      case "TechnicalReview":
      case "RouteDate":
      case "MovementDate":
        setShowDateRangeSection(true);
        break;
      case "Mandatory":
      case "VisibleOnMobile":
      case "IsInitial":
      case "IsFinal":
      case "NeedObservation":
      case "GeneratesPaymentToTechnician":
      case "Billable":
      case "RequestSignature":
      case "createNotification":
      case "IsPrivate":
      case "IsPublic":
        if (filter.FriendlyName === localize.edition_level) {
          switch (filter.Name) {
            case "IsPrivate":
              getIsPrivateOptions();
              break;

            default: // IsPublic
              getIsPublicOptions();
              break;
          }
        } else {
          getTrueFalseOptions();
        }
        setShowAutoComplete(true);
        break;
      case "OperationalArea":
        getOperationalAreas();
        setShowAutoComplete(true);
        break;
      case "Workflow":
        getWorkflows();
        setShowAutoComplete(true);
        break;
      case "Warehouse":
        getWarehouses();
        setShowAutoComplete(true);
        break;
      case "InventoryMovementType":
        getInventoryMovementTypes();
        setShowAutoComplete(true);
        break;
      case "ValueType":
        getFormFieldValueTypes();
        setShowAutoComplete(true);
        break;
      case "IdentificationType":
        getIdentificationTypes();
        setShowAutoComplete(true);
        break;
      case "ClientType":
        getClientTypesData();
        setShowAutoComplete(true);
        break;
      case "Proceeding":
        getProceeding();
        setShowAutoComplete(true);
        break;
      case "Provider":
        getProvider();
        setShowAutoComplete(true);
        break;
      case "RequestType":
        getRequestTypes();
        setShowAutoComplete(true);
        break;
      case "EventType":
        getEventType();
        setShowAutoComplete(true);
        break;
      case "PersonalAssigned":
        getPersonal();
        setShowAutoComplete(true);
        break;
      case "OperationalStatus":
        getOperationalStatus();
        setShowAutoComplete(true);
        break;
      case "CurrentStatus":
        getStatuses();
        setShowAutoComplete(true);
        break;
      case "Newness":
        getAdditionalNewness();
        setShowAutoComplete(true);
        break;
      case "State":
      case "City":
        getStates(filter);
        setShowAutoComplete(true);
        break;
      case "PaymentStatus":
        getPaymentstatus();
        setShowAutoComplete(true);
        break;
      case "PaymentMethod":
        getPaymentMethods();
        setShowAutoComplete(true);
        break;
      case "Contractor":
        getContractors();
        setShowAutoComplete(true);
        break;
      case "Client":
        getClients();
        setShowAutoComplete(true);
        break;
      case "RequestStatus":
        getRequestStatus();
        setShowAutoComplete(true);
        break;
      case "BillStatus":
        getBillStatus();
        setShowAutoComplete(true);
        break;
      case "MovementType":
        getMovementTypes();
        setShowAutoComplete(true);
        break;
      case "TotalValue":
      case "executionFinalValue":
      case "sparePartsFinalValue":
        setShowValueRange(true);
        break;
      default:
        setShowInputValue(true);
        break;
    }
  };

  const handleInputChange = (e) => {
    setSelectedFilter({
      ...selectedFilter,
      FilterValue: {
        Value: e.target.value,
      },
      Value: e.target.value,
    });
  };

  const handleStartDateChange = (value) => {
    try {
      const fieldValueData = {
        ...selectedFilter.FilterValue,
        Value: GetDateInYYYYMMDDWithoutTimeFormat(new Date(value), true, false),
      };

      setSelectedFilter({
        ...selectedFilter,
        FilterValue: fieldValueData,
        Value: `${GetDateInMMDDYYYYFormat(
          new Date(value),
        )} - ${GetDateInMMDDYYYYFormat(
          new Date(selectedFilter?.FilterValue?.AlternateValue),
        )}`,
      });
    } catch {
      console.log("Invalid date");
    }
  };

  const handleEndDateChange = (value) => {
    try {
      const fieldValueData = {
        ...selectedFilter.FilterValue,
        AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
          new Date(value),
          true,
          true,
        ),
      };

      setSelectedFilter({
        ...selectedFilter,
        FilterValue: fieldValueData,
        Value: `${GetDateInMMDDYYYYFormat(
          new Date(selectedFilter?.FilterValue?.Value),
        )} - ${GetDateInMMDDYYYYFormat(new Date(value))}`,
      });
    } catch {
      console.log("Invalid date");
    }
  };

  const handleAutoCompleteChangeForState = (value) => {
    setSelectedState(value);
  };

  useEffect(() => {
    if (selectedState) {
      getCitiesData(selectedState.Name);
    }
  }, [selectedState]);

  const handleAutoCompleteChange = (value) => {
    setSelectedFilterValue(value);

    if (value) {
      setSelectedFilter({
        ...selectedFilter,
        FilterValue: { Value: value.Id ?? value.id },
        Value:
          selectedFilter.Name === "PersonalAssigned"
            ? `${value.SurName} ${value.LastName}`
            : value.Name,
      });
      return;
    }

    setSelectedFilter(null);
  };

  const handleValueRangeValue = (e) => {
    if (e) {
      const newValue = e.target.value;
      const fieldValueData = {
        ...selectedFilter.FilterValue,
        Value: newValue,
      };

      setSelectedFilter({
        ...selectedFilter,
        FilterValue: fieldValueData,
        Value: `${getValueInCurrency(getCurrencyFromSetup(), newValue, true)} ${
          newValue === selectedFilter.FilterValue.AlternateValue
            ? ""
            : `- ${getValueInCurrency(
                getCurrencyFromSetup(),
                selectedFilter.FilterValue.AlternateValue,
                true,
              )}`
        }`,
      });
      return;
    }

    const fieldValueAlternativeData = {
      ...selectedFilter.FilterValue,
      Value: 0,
    };

    setSelectedFilter({
      ...selectedFilter,
      FilterValue: fieldValueAlternativeData,
      Value: `${getValueInCurrency(getCurrencyFromSetup(), 0, true)} ${
        selectedFilter.FilterValue.AlternateValue === 0
          ? ""
          : `- ${getValueInCurrency(
              getCurrencyFromSetup(),
              selectedFilter.FilterValue.AlternateValue,
              true,
            )}`
      }`,
    });
  };

  const handleValueRangeAlternateValue = (e) => {
    if (e) {
      const newValue = e.target.value;
      const filterValueData = {
        ...selectedFilter.FilterValue,
        AlternateValue: newValue,
      };

      setSelectedFilter({
        ...selectedFilter,
        FilterValue: filterValueData,
        Value: `${getValueInCurrency(
          getCurrencyFromSetup(),
          selectedFilter.FilterValue.Value,
          true,
        )} ${
          newValue === selectedFilter.FilterValue.Value
            ? ""
            : `- ${getValueInCurrency(getCurrencyFromSetup(), newValue, true)}`
        }`,
      });
      return;
    }

    const filterAlternateValueData = {
      ...selectedFilter.FilterValue,
      AlternateValue: 0,
    };
    setSelectedFilter({
      ...selectedFilter,
      FilterValue: filterAlternateValueData,
      Value: `${getValueInCurrency(
        getCurrencyFromSetup(),
        selectedFilter.FilterValue.Value,
      )} ${
        selectedFilter.FilterValue.Value === 0
          ? ""
          : `- ${getValueInCurrency(getCurrencyFromSetup(), 0)}`
      }`,
    });
  };

  const keyUp = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      onSubmit && onSubmit();
    }
  };

  const showAutoCompleteComponent = () => {
    if (selectedFilter?.Name === "City") {
      return (
        <Box className="flex flex-col gap-3 w-full">
          <Combobox
            id="Filter"
            fieldLabel={localize.department}
            fieldOptions={states?.Data ?? []}
            fieldValue={selectedState}
            handleValueChange={handleAutoCompleteChangeForState}
            popperFullWidth={PopperFullWidth}
            onkeyUp={keyUp}
          />

          <Combobox
            id="Filter"
            fieldLabel={selectedFilter?.FriendlyName}
            fieldOptions={filterValues?.Data ?? []}
            fieldValue={selectedFilterValue}
            handleValueChange={handleAutoCompleteChange}
            popperFullWidth={PopperFullWidth}
            onkeyUp={keyUp}
          />
        </Box>
      );
    }

    return (
      <Combobox
        id="Filter"
        fieldLabel={selectedFilter.FriendlyName}
        fieldOptions={filterValues?.Data ?? []}
        fieldValue={selectedFilterValue}
        optionFilter={
          selectedFilter.Name === "PersonalAssigned"
            ? (option, value) => option?.id === value?.id
            : null
        }
        customOptionLabel={
          selectedFilter.Name === "PersonalAssigned"
            ? (option) =>
                option.id === 0
                  ? `${option.Name}`
                  : `${option.Identification} - ${option.SurName} ${option.LastName}`
            : selectedFilter.Name === "Client"
            ? (option) =>
                option.Id === 0
                  ? `${option.Name}`
                  : `${option.IdentificationType}: ${option.Identification} - ${option.Name}`
            : null
        }
        handleValueChange={handleAutoCompleteChange}
        popperFullWidth={PopperFullWidth}
        onkeyUp={keyUp}
      />
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <section style={{ width: "100%" }}>
        <div className="flex justify-between my-4">
          <Combobox
            customOptionLabel={(option) => option.FriendlyName}
            fieldLabel={localize.select_filter}
            fieldOptions={filterOptionsToSelect ?? []}
            fieldValue={selectedFilter}
            id="FilterType"
            handleValueChange={handleFilterTypeOnChange}
            optionFilter={(option, value) => option.Name === value.Name}
            popperFullWidth={PopperFullWidth}
          />
        </div>

        {showInputSection && (
          <div className="flex justify-between">
            {isLoading ? (
              <Typography>{`${localize.loading}...`}</Typography>
            ) : (
              <div className="w-full">
                {/* Event-type Work-team Operational-status Execution-status Bill-status Payment-Status */}
                {showAutoComplete && showAutoCompleteComponent()}
                {/* Input */}
                {showInputValue && (
                  <TextField
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name={selectedFilter?.Name}
                    id={selectedFilter?.Name}
                    label={selectedFilter?.FriendlyName}
                    variant="outlined"
                    onChange={handleInputChange}
                    onKeyUp={keyUp}
                    value={selectedFilter?.FilterValue?.Value ?? ""}
                  />
                )}
                {/* Number range values */}
                {showValueRange && (
                  <div
                    className="w-full flex justify-between"
                    style={{ marginRight: 20 }}
                  >
                    <TextField
                      type="number"
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      value={selectedFilter.FilterValue.Value}
                      onChange={handleValueRangeValue}
                      size="small"
                      id="dateRangeValue"
                      name="dateRangeValue"
                      label={localize.minimum_value}
                      variant="outlined"
                      fullWidth
                      onKeyUp={keyUp}
                    />

                    <div className="mx-2" />

                    <TextField
                      type="number"
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      value={selectedFilter.FilterValue.AlternateValue}
                      onChange={handleValueRangeAlternateValue}
                      size="small"
                      id="dateRangeAlternateValue"
                      name="dateRangeAlternateValue"
                      label={localize.maximum_value}
                      variant="outlined"
                      fullWidth
                      onKeyUp={keyUp}
                    />
                  </div>
                )}
                {/* Date ranges */}
                {showDateRangeSection && (
                  <div
                    className="w-full flex justify-between"
                    style={{ marginRight: 20 }}
                  >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label={`${localize.start_date}`}
                        className="w-full"
                        InputLabelProps={getInputLabelProps()}
                        inputProps={getInputProps()}
                        PopperComponent={PopperFullWidth}
                        value={selectedFilter?.FilterValue?.Value}
                        onChange={handleStartDateChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              placeholder: "",
                            }}
                            InputLabelProps={{
                              ...params.InputLabelProps,
                              style: { fontSize: "1em", paddingBottom: "10px" },
                            }}
                            onKeyUp={keyUp}
                          />
                        )}
                      />
                    </LocalizationProvider>

                    <div className="mx-2" />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label={`${localize.end_date}`}
                        className="w-full"
                        InputLabelProps={getInputLabelProps()}
                        inputProps={getInputProps()}
                        PopperComponent={PopperFullWidth}
                        value={selectedFilter?.FilterValue?.AlternateValue}
                        onChange={handleEndDateChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              placeholder: "",
                            }}
                            InputLabelProps={{
                              ...params.InputLabelProps,
                              style: { fontSize: "1em", paddingBottom: "10px" },
                            }}
                          />
                        )}
                        onKeyUp={keyUp}
                      />
                    </LocalizationProvider>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {selectedFilter?.Note?.length > 0 && (
          <Alert severity="info" className="w-full my-2">
            <label className="text-xs">{selectedFilter?.Note}</label>
          </Alert>
        )}
      </section>
    </div>
  );
}
